// HomeTab.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Zap, Users, Sparkles, Heart, MessageCircle, Sun, UserCircle } from "lucide-react";
import AstriaMexicoShell, { AstriaMexicoTab } from "./AstriaMexicoShell";
import { useMexicoColors } from "./AstriaMexicoThemeContext";

// ─── Astria Mexico — module picker landing screen. Simple grid of the 7
// modules (no zodiac-wheel gimmick, this lane doesn't spec one) so a first-
// time visitor immediately sees what's available and how to start.

interface ModuleDef {
  tab: AstriaMexicoTab;
  path: string;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const MODULES: ModuleDef[] = [
  {
    tab: "energy-match",
    path: "/astria-mexico/energy-match",
    label: "mx.tabs.energyMatch",
    description: "mx.home.desc.energyMatch",
    icon: <Zap className="w-5 h-5" />,
  },
  {
    tab: "compatibility",
    path: "/astria-mexico/compatibility",
    label: "mx.tabs.compatibility",
    description: "mx.home.desc.compatibility",
    icon: <Users className="w-5 h-5" />,
  },
  {
    tab: "cosmic-message",
    path: "/astria-mexico/cosmic-message",
    label: "mx.tabs.cosmicMessage",
    description: "mx.home.desc.cosmicMessage",
    icon: <Sparkles className="w-5 h-5" />,
  },
  {
    tab: "relationship",
    path: "/astria-mexico/relationship",
    label: "mx.tabs.relationship",
    description: "mx.home.desc.relationship",
    icon: <Heart className="w-5 h-5" />,
  },
  {
    tab: "companion-talk",
    path: "/astria-mexico/companion-talk",
    label: "mx.tabs.companionTalk",
    description: "mx.home.desc.companionTalk",
    icon: <MessageCircle className="w-5 h-5" />,
  },
  {
    tab: "daily-flow",
    path: "/astria-mexico/daily-flow",
    label: "mx.tabs.dailyFlow",
    description: "mx.home.desc.dailyFlow",
    icon: <Sun className="w-5 h-5" />,
  },
  {
    tab: "zodiac-personality",
    path: "/astria-mexico/zodiac-personality",
    label: "mx.tabs.personality",
    description: "mx.home.desc.personality",
    icon: <UserCircle className="w-5 h-5" />,
  },
];

const HomeInner: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { colors } = useMexicoColors();

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: colors.titleColor, marginBottom: 6 }}>
          {t("mx.home.welcome")}
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          {t("mx.home.intro")}
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 12,
        }}
      >
        {MODULES.map((mod) => (
          <button
            key={mod.tab}
            onClick={() => navigate(mod.path)}
            className="text-left transition-all hover:scale-[1.02]"
            style={{
              padding: 18,
              borderRadius: 14,
              border: `1px solid ${colors.cardBorder}`,
              background: colors.card,
            }}
          >
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                border: `1.5px solid ${colors.accent}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: colors.accent,
                marginBottom: 12,
              }}
            >
              {mod.icon}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: colors.titleColor, marginBottom: 4 }}>
              {t(mod.label)}
            </div>
            <div style={{ fontSize: 12.5, color: colors.mutedColor, lineHeight: 1.5 }}>
              {t(mod.description)}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const HomeTab: React.FC = () => (
  <AstriaMexicoShell active="home">
    <HomeInner />
  </AstriaMexicoShell>
);

export default HomeTab;
